import * as ko from "knockout";
import { type Computed, type components, type Observable, observable } from "knockout";

import * as Backend from "../../../backend/v1/sdk.gen";
import { translated } from "../../../lib/localize";
import type { HtmlDialog } from "../../../lib/popups";
import { htmlDialogStarter } from "../../dialogStarter";
import type { CageId, CageItem } from "./cagePicker";
import type { LocationCagePickerParams } from "./locationCagePicker";
import { type LocationIdString, type LocationItem, type PreselectLocationItem, typeIdMap } from "./locationPicker";

/**
 * Location Cage Picker Popup Component
 *
 * Wrapping location-cage-picker in popup dialog using dialog bindingHandler.
 * All params of location-cage-picker will be passed through - read picker doc string.
 *
 * @param className - Additional class name(s) that will be assigned to
 * the container element that triggers the popup to open (f.e. for sizing)
 *
 * @param inputClassName - Additional class name(s) that will be assigned to the input
 * element that triggers the popup to open.
 *
 * @param inputId - String that should be used as id attribute on the input element
 * that triggers the popup to open. This can be usefull if used in forms with label
 * elements.
 *
 * @param afterApply - Function that will be executed when location picker selection
 * is applied/confirmed.
 */
interface LocationCagePickerPopupParams extends LocationCagePickerParams {
    className?: Record<string, () => any>;
    inputClassName?: string;
    inputId?: string;
    afterApply?: () => void;
}

const showLocationCagePickerPopup = htmlDialogStarter(
    class {
        dialog: HtmlDialog;
        model: LocationCagePickerPopupViewModel;

        constructor(
            dialog: HtmlDialog,
            params: {
                title: string;
                eventTarget: HTMLElement;
                model: LocationCagePickerPopupViewModel;
                onClose: () => void;
            },
        ) {
            this.dialog = dialog;
            this.model = params.model;
        }
    },
    `
        <div class="location-cage-picker-popup popup_frame" data-bind="with: model">
                <ko-location-cage-picker params="pickerClassName: pickerClassName,
                                                 initialized: initialized,
                                                 selectedLocation: selectedLocation,
                                                 selectedCage: selectedCage,
                                                 parentLocation: parentLocation,
                                                 preselectLocation: preselectLocation,
                                                 preselectCage: preselectCage,
                                                 unselectLocationTrigger: unselectLocationTrigger,
                                                 unselectCageTrigger: unselectCageTrigger,
                                                 cleanupTrigger: cleanupTrigger,
                                                 selectedLocationTitle: selectedLocationTitle,
                                                 selectedCageTitle: selectedCageTitle,
                                                 rememberSelectionTrigger: rememberSelectionTrigger,
                                                 restoreSelectionTrigger: restoreSelectionTrigger"></ko-location-cage-picker>
                <div class="popup_footer">
                    <input type="reset"
                           data-bind="value: translated('Cancel'),
                                      click: hidePopup"/>
                    <input type="submit"
                           class="confirm"
                           data-bind="value: translated('Apply'),
                                      click: confirmSelect,
                                      enable: confirmable"/>
                </div>
        </div>
    `,
    (params) => ({
        title: params.title,
        onClose: params.onClose,
        position: { anchor: params.eventTarget },
        width: 600,
    }),
);

class LocationCagePickerPopupViewModel {
    dialog: ReturnType<typeof showLocationCagePickerPopup>;
    public componentElement: HTMLElement;
    public initialized: Observable<false | "loading" | true>;
    public className: Record<string, () => any>;
    public pickerClassName: string;
    public inputClassName: string;
    public inputId: string;
    public isConfirmed: boolean;
    public disabledPicker: any;
    public disabledAttr: Observable<string | boolean>;
    public rememberSelectionTrigger: Observable;
    public restoreSelectionTrigger: Observable;
    public parentLocation: LocationIdString;
    public selectedLocation: Observable<LocationItem>;
    public selectedCage: Observable<CageItem>;
    public preselectLocation: PreselectLocationItem;
    public preselectCage: CageId;
    public unselectLocationTrigger: Observable;
    public unselectCageTrigger: Observable;
    public cleanupTrigger: Observable;
    public selectedLocationTitle: Observable<string>;
    public selectedCageTitle: Observable<string>;
    public selectedCageText: Computed<any>;
    public afterApply: () => void;
    public confirmable: Computed<boolean>;

    constructor(params: LocationCagePickerPopupParams, componentElement: HTMLElement) {
        this.componentElement = componentElement;
        this.initialized = ko.observable();
        this.className = params.className;
        this.pickerClassName = params.pickerClassName;
        this.inputClassName = params.inputClassName;
        this.inputId = params.inputId;
        this.isConfirmed = false;
        this.disabledPicker = params.disabled;
        this.disabledAttr = ko.observable(this.disabledPicker ? "disabled" : false);
        this.rememberSelectionTrigger = ko.observable();
        this.restoreSelectionTrigger = ko.observable();
        this.parentLocation = params.parentLocation;
        this.selectedLocation = params.selectedLocation;
        this.selectedCage = params.selectedCage;
        this.preselectLocation = params.preselectLocation;
        this.preselectCage = params.preselectCage;
        this.unselectLocationTrigger = params.unselectLocationTrigger;
        this.unselectCageTrigger = params.unselectCageTrigger;
        this.cleanupTrigger = ko.isObservable(params.cleanupTrigger) ? params.cleanupTrigger : ko.observable();
        this.selectedLocationTitle = ko.isObservable(params.selectedLocationTitle)
            ? params.selectedLocationTitle
            : ko.observable();
        this.selectedCageTitle = ko.isObservable(params.selectedCageTitle) ? params.selectedCageTitle : ko.observable();
        this.selectedCageText = ko.pureComputed(() => {
            return this.initialized() === "loading"
                ? translated("Loading")
                : this.selectedCage()
                  ? this.selectedCage().cagenumber
                  : this.disabledPicker
                    ? translated("No location set")
                    : "";
        });
        this.selectedCage.subscribe(() => {
            const x = this.selectedCage();
            let s = "";

            if (x instanceof Object) {
                s = this.selectedLocationTitle();
                s += "\n" + translated("Cage") + ": " + x.cagenumber;
            }
            this.selectedCageTitle(s);
        });

        this.confirmable = ko.pureComputed(() => {
            return this.selectedLocation() instanceof Object && this.selectedCage() instanceof Object;
        });

        if (ko.isObservable(params.initialized)) {
            params.initialized(this.initialized());
            this.initialized.subscribe((value) => {
                params.initialized(value);
            });
        }

        if (this.preselectLocation?.id) {
            Backend.getPickerLocation({
                path: { location_type: this.preselectLocation.type, location_id: this.preselectLocation.id },
            }).then(({ data: location }) => {
                const locationIdName = typeIdMap[location.type];
                this.selectedLocation({
                    active: observable(true),
                    locationId: { [locationIdName]: location[locationIdName] },
                    ...location,
                });
            });
        }

        if (this.preselectCage) {
            Backend.getPickerCage({
                path: { cage_id: this.preselectCage },
            }).then(({ data: cage }) => {
                const cagenumberSplit = cage.cagenumber.split("-");
                this.selectedCage({
                    prefix: cagenumberSplit[0],
                    suffix: cagenumberSplit[1],
                    selected: observable(true),
                    ...cage,
                });
            });
        }

        this.afterApply = () => {
            if (typeof params.afterApply === "function") {
                params.afterApply();
            }
        };
    }

    public showPopup = () => {
        if (this.disabledPicker) {
            return;
        }
        this.dialog = showLocationCagePickerPopup({
            eventTarget: this.componentElement,
            model: this,
            title: translated("Select cage"),
            onClose: this.closeCallback,
        });
        this.cleanupTrigger.valueHasMutated();
        this.rememberSelectionTrigger.valueHasMutated();
    };

    public hidePopup = () => {
        this.dialog?.dialog?.close();
    };

    public confirmSelect = () => {
        this.isConfirmed = true;

        this.afterApply();
        this.hidePopup();
    };

    private closeCallback = () => {
        if (!this.isConfirmed) {
            this.restoreSelectionTrigger.valueHasMutated();
        }
        //lose focus of cage location (textarea), so location-picker-popup shows of on next click
        const blurEvent = document.createEvent("HTMLEvents");
        blurEvent.initEvent("blur", true, false);
        this.componentElement.dispatchEvent(blurEvent);
    };
}

export class LocationCagePickerPopupComponent {
    constructor() {
        // biome-ignore lint/correctness/noConstructorReturn: <explanation>
        return {
            viewModel: {
                createViewModel: (params: LocationCagePickerPopupParams, componentInfo: components.ComponentInfo) => {
                    let viewModelElement: ChildNode;
                    const componentElement = componentInfo.element as HTMLElement;
                    if (componentElement.nodeName === "#comment") {
                        viewModelElement = componentElement.nextElementSibling;
                    } else {
                        viewModelElement = componentElement.firstChild;
                    }
                    return new LocationCagePickerPopupViewModel(params, viewModelElement as HTMLElement);
                },
            },
            template: `
                <div class="tw:flex tw:gap-1"
                    data-bind="css: className">
                    <input type="text"
                           class="tw:grow"
                           readonly="readonly"
                           data-bind="value: selectedCageText,
                                      attr: {title: selectedCageTitle,
                                             id: inputId,
                                             disabled: disabledAttr},
                                      css: inputClassName"/>
                    <button type="button"
                            data-bind="click: showPopup,
                                       hidden: disabledPicker,
                                       text: translated('Select')"></button>
                </div>
            `,
        };
    }
}
